import { createTheme } from "@mui/material/styles";

declare global {
  interface InputProps {
    colorOverride: string;
  }
}

const theme = createTheme({
  components: {
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: "#d2d4d5",
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          alignSelf: "flex-start",
          marginBottom: "0.5rem",
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          boxShadow: "none",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        root: {
          marginBottom: "1rem",
          width: "100%",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: "#055776",
          color: "#f8f8f8",
          "&:focus": {
            backgroundColor: "#1d8815",
          },
          "&:focusVisible": {
            backgroundColor: "#1d8815",
          },
        },
        deleteIcon: {
          color: "#f8f8f8",
        },
      },
    },
    MuiSpeedDial: {
      styleOverrides: {
        fab: {
          height: "48px",
          width: "48px",
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.coloroverride === "white" && {
            color: "#f8f8f8 !important",
          }),
        }),
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.coloroveride !== "white" && {
            color: "#055776",
          }),
          ...(ownerState.coloroverride === "white" && {
            color: "#f8f8f8 !important",
          }),
        }),
      },
    },
    MuiInputBase: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.coloroveride !== "white" && {
            color: "#055776",
          }),
          ...(ownerState.coloroverride === "white" && {
            color: "#f8f8f8",
          }),
        }),
      },
    },
    MuiInput: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          "&.no-border": {
            borderBottom: "none !important",
            "&:hover": {
              borderBottom: "none !important",
            },
          },
          ...(ownerState.coloroverride === "white" && {
            "&:before": {
              borderBottom: "3px solid #f8f8f8",
            },
            "&:after": {
              borderBottomWidth: 3,
            },
            "&:focus": {
              "&:not(.Mui-disabled)": {
                "&:before": {
                  borderBottom: "3px solid #58b947",
                },
                "&:after": {
                  borderBottom: "3px solid #58b947",
                },
              },
              "&:after": {
                borderBottom: "3px solid #58b947",
              },
            },
            "&:hover": {
              "&:not(.Mui-disabled)": {
                "&:before": {
                  borderBottom: "3px solid #58b947",
                },
                "&:after": {
                  borderBottom: "3px solid #58b947",
                },
              },
              "&:after": {
                borderBottom: "3px solid #58b947",
              },
            },
          }),
        }),
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          ...(ownerState.coloroverride === "white" && {
            color: "#f8f8f8",
          }),
        }),
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          cursor: "pointer",
        },
      },
    },
    MuiCardContent: {
      styleOverrides: {
        root: {
          paddingTop: 0,
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        root: {
          height: 10,
          justifyContent: "center",
          top: "42vh",
          width: "100%",
          zIndex: 99999,
        },
        dashed: {
          backgroundImage:
            "radial-gradient(rgb(160, 191, 202) 0%, rgb(160, 191, 202) 25%, transparent 42%)",
          backgroundSize: "17px 19px",
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          "&.search": {
            color: "#f8f8f8",
          },
        },
      },
    },
    MuiToolbar: {
      styleOverrides: {
        root: {
          padding: "0 !important",
        },
      },
    },
  },
  palette: {
    primary: {
      main: "#055776",
      light: "#002d49",
      dark: "#002d49",
    },
    secondary: {
      main: "#58b947",
      light: "#8cec76",
      dark: "#1d8815",
    },
  },
});

if (theme.components) {
  // component overrides that depend on theme initialization
  theme.components.MuiTab = {
    styleOverrides: {
      root: {
        [theme.breakpoints.down("md")]: {
          maxWidth: "none",
          webkitBoxFlex: "1",
          webkitFlexGrow: "1",
          msFlexPositive: "1",
          flexGrow: "1",
          webkitFlexBasis: "0",
          msFlexPreferredSize: "0",
          flexBasis: "0",
        },
        [theme.breakpoints.down("sm")]: {
          maxWidth: 360,
          webkitBoxFlex: "auto",
          webkitFlexGrow: "auto",
          msFlexPositive: "auto",
          flexGrow: "auto",
          webkitFlexBasis: "auto",
          msFlexPreferredSize: "auto",
          flexBasis: "auto",
        },
      },
    },
  };

  theme.components.MuiDrawer = {
    styleOverrides: {
      root: {
        "& .MuiDrawer-paper": {
          backgroundColor: "#4683a4",
          boxShadow:
            "3px 0 5px -5px rgb(0 0 0 / 50%), 5px 0 10px -5px rgb(0 0 0 / 50%)",
          borderRight: 0,
          color: "#f8f8f8",
          [theme.breakpoints.up("lg")]: {
            backgroundColor: "#d2d4d5",
            color: "#000000",
          },
        },
      },
    },
  };

  theme.components.MuiListItem = {
    styleOverrides: {
      root: {
        [theme.breakpoints.down("sm")]: {
          flexDirection: "column",
          margin: "1em",
        },
      },
    },
  };

  theme.components.MuiListItemSecondaryAction = {
    styleOverrides: {
      root: {
        [theme.breakpoints.down("sm")]: {
          margin: "1em",
          position: "relative",
        },
      },
    },
  };

  theme.components.MuiListItemIcon = {
    styleOverrides: {
      root: {
        color: "#f8f8f8",
        [theme.breakpoints.up("lg")]: {
          color: "#000000",
        },
      },
    },
  };

  theme.components.MuiGrid = {
    styleOverrides: {
      root: {
        [theme.breakpoints.down("md")]: {
          justifyContent: "flex-start",
          textAlign: "left",
        },
        "&.loader": {
          backgroundColor: "rgba(255, 255, 255, 0.8)",
          display: "flex",
          height: "100vh",
          justifyContent: "center",
          position: "fixed",
          width: "100vw",
          zIndex: 99998,
        },
        "&.hide": {
          display: "none",
        },
        "&.search": {
          display: "flex",
          justifyContent: "center",
        },
        "&.disabled": {
          backgroundColor: "rgba(225, 225, 225, 0.9)",
          cursor: "help",
        },
      },
    },
  };

  (theme.components.MuiButton = {
    styleOverrides: {
      root: {
        "&.search": {
          alignSelf: "center",
          [theme.breakpoints.down("sm")]: {
            alignSelf: "flex-start",
          },
        },
        "&.Mui-disabled": {
          pointerEvents: "auto",
        },
      },
    },
    variants: [
      {
        props: { variant: "text", color: "secondary" },
        style: {
          color: theme.palette.secondary.light,
        },
      },
      {
        props: { variant: "outlined", color: "secondary" },
        style: {
          color: theme.palette.secondary.light,
        },
      },
    ],
  }),
    (theme.components.MuiTextField = {
      styleOverrides: {
        root: ({ ownerState }) => ({
          marginBottom: "0.5rem",
          "&.noResults": {
            marginTop: "25% !important",
          },
          "&.no-border": {
            '& [class*="MuiInputBase-root"]': {
              fontSize: "1.5em",
              "&:before": {
                border: "none !important",
              },
            },
          },
          input: {
            color: theme.palette.primary,
          },
          label: {
            color: theme.palette.primary,
          },
          ...(ownerState.className === "search" && {
            '& [class*="MuiInputBase-root"]': {
              "&:before": {
                borderBottom: "3px solid #f8f8f8",
              },
              "&:after": {
                borderBottomWidth: 3,
              },
              "&:focus": {
                "&:not(.Mui-disabled)": {
                  "&:before": {
                    borderBottom: "3px solid #58b947",
                  },
                  "&:after": {
                    borderBottom: "3px solid #58b947",
                  },
                },
                "&:after": {
                  borderBottom: "3px solid #58b947",
                },
              },
              "&:hover": {
                "&:not(.Mui-disabled)": {
                  "&:before": {
                    borderBottom: "3px solid #58b947",
                  },
                  "&:after": {
                    borderBottom: "3px solid #58b947",
                  },
                },
                "&:after": {
                  borderBottom: "3px solid #58b947",
                },
              },
              "&.searchText": {
                fontSize: "3em",
                [theme.breakpoints.down("sm")]: {
                  fontSize: "1.5em",
                },
              },
            },
            input: {
              color: "#f8f8f8 !important",
              webkitAppearance: "none !important",
              margin: "0 !important",
              mozAppearance: "textfield !important",
              "&type=number": {
                webkitAppearance: "none !important",
                margin: "0 !important",
                mozAppearance: "textfield !important",
              },
            },
            label: {
              color: "#f8f8f8 !important",
              "&.searchLabelText": {
                lineHeight: "6em",
                [theme.breakpoints.down("sm")]: {
                  lineHeight: "2em",
                },
              },
              "&.searchLabelTextFocus": {
                lineHeight: "1em",
              },
            },
          }),
        }),
      },
    });

  theme.components.MuiLink = {
    styleOverrides: {
      root: {
        "&.logo": {
          [theme.breakpoints.down("sm")]: {
            display: "none",
          },
        },
        "&.smallLogo": {
          [theme.breakpoints.down("sm")]: {
            display: "flex",
          },
          [theme.breakpoints.up("sm")]: {
            display: "none",
          },
        },
      },
    },
  };

  theme.components.MuiAccordionSummary = {
    styleOverrides: {
      root: {
        margin: 0,
        backgroundColor: "rgba(0, 0, 0, 0.1)",
        "&.Mui-expanded": {
          margin: 0,
          minHeight: 56,
        },
      },
      content: {
        "&.Mui-expanded": {
          margin: 0,
        },
      },
    },
  };
}

export default theme;
